import { Controller } from "@hotwired/stimulus"
import { fill } from "../../lib/format"

// A wall-clock time in one IANA zone, read in another. Uses the browser's own
// Intl data, so daylight saving is handled on both sides.
export default class extends Controller {
  static targets = ["from", "when", "to", "zones", "result", "difference", "status"]
  static values = { messages: Object }

  connect() {
    const zones = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : []
    this.zonesTarget.replaceChildren(...zones.map(zone => Object.assign(document.createElement("option"), { value: zone })))

    const local = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!this.fromTarget.value) this.fromTarget.value = local || ""
    if (!this.whenTarget.value) this.whenTarget.value = localInputValue(new Date())
    this.convert()
  }

  convert() {
    const from = this.zone(this.fromTarget.value)
    const to = this.zone(this.toTarget.value)
    const parts = this.whenTarget.value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)

    if (!from || !to || !parts) {
      this.resultTarget.textContent = "—"
      this.differenceTarget.textContent = "—"
      this.statusTarget.textContent = !parts ? this.messagesValue.pick_time : this.messagesValue.pick_zones
      return
    }

    const [, y, mo, d, h, mi] = parts.map(Number)
    const instant = zonedToInstant(y, mo, d, h, mi, from)
    const there = new Intl.DateTimeFormat("en-GB", {
      timeZone: to, weekday: "short", day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false
    }).format(instant)

    const offset = (offsetMinutes(instant, to) - offsetMinutes(instant, from)) / 60
    const sign = offset > 0 ? "+" : offset < 0 ? "−" : ""
    const hours = Math.abs(offset)

    this.resultTarget.textContent = there
    this.differenceTarget.textContent = offset === 0 ? this.messagesValue.same_time : fill(this.messagesValue.hours, { hours: `${sign}${hours % 1 ? hours.toFixed(1) : hours}` })
    this.statusTarget.textContent = fill(this.messagesValue.summary, { from_abbrev: abbrev(instant, from), from, to_abbrev: abbrev(instant, to), to })
  }

  // A zone name the browser recognises, or null.
  zone(name) {
    const trimmed = name.trim()
    if (!trimmed) return null
    try {
      return Intl.DateTimeFormat(undefined, { timeZone: trimmed }).resolvedOptions().timeZone
    } catch {
      return null
    }
  }
}

// Offset of `zone` from UTC at `instant`, in minutes.
function offsetMinutes(instant, zone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone, hourCycle: "h23",
    year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric"
  }).formatToParts(instant)
  const get = type => Number(parts.find(p => p.type === type).value)
  const asUTC = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"))
  return Math.round((asUTC - instant.getTime()) / 60000)
}

// The instant at which a wall clock in `zone` reads the given components.
// Guess the offset, then correct once for the case where the guess straddled
// a daylight saving change.
function zonedToInstant(y, mo, d, h, mi, zone) {
  const wall = Date.UTC(y, mo - 1, d, h, mi)
  let instant = new Date(wall - offsetMinutes(new Date(wall), zone) * 60000)
  instant = new Date(wall - offsetMinutes(instant, zone) * 60000)
  return instant
}

function abbrev(instant, zone) {
  return new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "short" })
    .formatToParts(instant).find(p => p.type === "timeZoneName").value
}

function localInputValue(date) {
  const pad = n => String(n).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}
